import React, { useState, useEffect, useRef } from "react";

import { UseAppDispatch } from "redux/store";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Checkbox,
  Drawer,
  FormControlLabel,
  IconButton,
  Paper,
  Popover,
  Stack,
  SvgIcon,
  SxProps,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Icon } from "@iconify/react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import CloseIcon from "@mui/icons-material/Close";
import RemoveShoppingCartIcon from "@mui/icons-material/RemoveShoppingCart";
import {
  IPassengerTypeShort,
  formatPrice,
  passengerMapShort,
  pushToDataLayer,
  useConfig,
} from "@thekilroygroup/shared-ui";
import {
  additionalProductsSelector,
  setAdditionalProducts,
  toggleAdditionalProduct,
  IAdditionalProduct,
} from "redux/additionalProducts";
import { useTranslations } from "hooks/useTranslations";
import {
  addPaymentLine,
  removePaymentLine,
  IPaymentLine,
} from "redux/shoppingCart";
import {
  travelerInformationSelector,
  ITraveler,
} from "redux/travelerInformation";
import { itinerarySelector } from "redux/selectedItinerary";
import { IProduct } from "hooks/translations.interfaces";
import RichText from "utils/RichTextEditorOutput";

interface IAdditionalProductsProps {
  validateHandler: (valid: boolean) => void;
  handleProceed: () => void;
  initialValidState: boolean;
}

export default function AdditionalProducts(props: IAdditionalProductsProps) {
  const { validateHandler, handleProceed, initialValidState } = props;

  const {
    translations,
    translations: {
      dynamicContent,
      dynamicContent: { products },
    },
  } = useTranslations();

  const { locale } = useConfig();
  const dispatch = UseAppDispatch();
  const additionalProducts = useSelector(additionalProductsSelector);
  const [isValid, setIsValid] = useState(initialValidState);
  const [showInfo, setShowInfo] = useState<string>("");

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const { travelers } = useSelector(travelerInformationSelector);

  const { products: itineraryAdditionalProducts } =
    useSelector(itinerarySelector);

  const getProductContent = (productCode: string, property: string) => {
    const content: IProduct | undefined = dynamicContent.products.find(
      (product: IProduct) => product.productCode === productCode,
    );
    return (content && content[property as keyof IProduct]) || productCode;
  };

  const handleToggleProduct = (
    product: IAdditionalProduct,
    productIndex: number,
    addToAll: boolean,
    travelerIndex?: number,
  ) => {
    pushToDataLayer(
      `Code: ${product.productCode}, Scope: ${product.scope}`,
      "Toggle additional service",
    );

    dispatch(
      toggleAdditionalProduct({
        product: product,
        productIndex: productIndex,
        travelerIndex: travelerIndex,
        travelersTotal: travelers.length,
        addToAll: addToAll,
        travelers: travelers,
      }),
    );
  };

  const setupProducts = () => {
    const additionalProductsList = itineraryAdditionalProducts.map(
      (product) => ({
        ...product,
        selectedAll: false,
        selectedList: [],
      }),
    );
    additionalProductsList.sort((a, b) => {
      return a.sortingPriority - b.sortingPriority;
    });
    dispatch(setAdditionalProducts(additionalProductsList));
  };

  useEffect(() => {
    const hasToggledProducts = additionalProducts.products.filter(
      (product) => product.selectedList.length > 0,
    );
    setIsValid(hasToggledProducts.length > 0);

    additionalProducts.products.forEach((product) => {
      if (product.selectedList.length > 0) {
        const label = `${getProductContent(product.productCode, "title")} X ${
          product.selectedList.length
        }`;
        const paymentLine: IPaymentLine = {
          key: product.productCode,
          label: label,
          amount: calcProductPrice(product),
          index: -1,
        };
        dispatch(addPaymentLine(paymentLine));
      } else {
        dispatch(
          removePaymentLine({
            key: product.productCode,
          }),
        );
      }
    });
  }, [additionalProducts]);

  useEffect(() => {
    setupProducts();
    validateHandler(isValid);
  }, []);

  const isProductAvailableForTraveler = (
    product: IAdditionalProduct,
    traveler: ITraveler,
  ) => {
    const isAvailable =
      traveler.passengerType &&
      product.passengerPriceList.some(
        (p) =>
          p.passengerGroup ===
          passengerMapShort[traveler.passengerType as IPassengerTypeShort],
      );
    if (isAvailable) {
      return true;
    }
    return false;
  };

  const calcProductPricePerPassenger = (
    product: IAdditionalProduct,
    traveler: ITraveler,
  ) => {
    const pass = product.passengerPriceList.find(
      (pr) =>
        pr.passengerGroup ===
        passengerMapShort[traveler.passengerType as IPassengerTypeShort],
    );
    return pass?.price;
  };

  const calcProductPrice = (product: IAdditionalProduct) => {
    if (product.scope === "PASSENGER") {
      const t = travelers.forEach((t) => {
        passengerMapShort[t.passengerType as IPassengerTypeShort];
      });
      let priceTotal = 0;
      const p = product.selectedList.forEach((p) => {
        const traveler = travelers[p as number];
        const travelerType =
          passengerMapShort[traveler.passengerType as IPassengerTypeShort];
        const price = product.passengerPriceList.find(
          (pr) => pr.passengerGroup === travelerType,
        )?.price;
        priceTotal += price as number;
      });
      return priceTotal;
    }
    return product.bookingPrice;
  };

  const clickNextButton = () => {
    pushToDataLayer("Step 3 - Addditional services", "Click next step");
    handleProceed();
  };

  const elementRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  const saveRef = (id: string, ref: HTMLButtonElement | null) => {
    elementRefs.current[id] = ref;
  };

  return (
    <Box>
      <Typography sx={sxStyles.description}>
        {translations.additionalProducts.description}
      </Typography>
      {!isDesktop && (
        <Box
          sx={
            {
              display: "flex",
              justifyContent: "flex-end",
              ...sxStyles.skipStepButtonContainer,
            } as SxProps
          }
        >
          <Button
            color="primary"
            size="large"
            variant="outlined"
            disableElevation
            onClick={clickNextButton}
          >
            {isValid
              ? translations.navigation.nextStep
              : translations.navigation.skipThisStep}
          </Button>
        </Box>
      )}
      <Box sx={sxStyles.formWrapper}>
        {additionalProducts.products.map((product, productIndex) => (
          <Paper elevation={2} key={productIndex} sx={sxStyles.productItem}>
            <Box sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center">
                <Box sx={sxStyles.productItemIcon}>
                  <SvgIcon color="primary" fontSize="medium">
                    <Icon
                      icon={
                        products.find(
                          (p) => p.productCode === product.productCode,
                        )?.icon || ""
                      }
                    />
                  </SvgIcon>
                </Box>
                <Typography
                  variant="titleMedium"
                  weight="bold"
                  sx={sxStyles.productItemTitle}
                >
                  {getProductContent(product.productCode, "title")}
                  <IconButton
                    onClick={() => setShowInfo(product.productCode)}
                    ref={(ref) => saveRef(product.productCode, ref)}
                    sx={{ marginLeft: 1, bgcolor: "" }}
                    color="primary"
                  >
                    <InfoOutlinedIcon color="primary" fontSize="small" />
                  </IconButton>
                </Typography>
                <Typography
                  variant="titleMedium"
                  weight="bold"
                  sx={sxStyles.productItemPrice}
                >
                  {formatPrice(
                    product.scope === "BOOKING"
                      ? product.bookingPrice
                      : product.passengerPriceList[0].price,
                    locale,
                    product.currency,
                  )}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="end"
                sx={sxStyles.productRow}
              >
                <Box sx={{ maxWidth: 400 }}>
                  <RichText
                    htmlContent={getProductContent(
                      product.productCode,
                      "description",
                    )}
                  />
                </Box>
                <Button
                  sx={sxStyles.productButton}
                  color="primary"
                  size="medium"
                  variant={product.selectedAll ? "contained" : "outlined"}
                  onClick={() =>
                    handleToggleProduct(product, productIndex, true)
                  }
                >
                  {product.selectedAll ? (
                    <>
                      <RemoveShoppingCartIcon fontSize="small" />
                      {product.scope === "PASSENGER" && travelers.length > 1
                        ? translations.additionalProducts.removeFromAll
                        : translations.additionalProducts.remove}
                    </>
                  ) : (
                    <>
                      <AddShoppingCartIcon color="primary" fontSize="small" />
                      {product.scope === "PASSENGER" && travelers.length > 1
                        ? translations.additionalProducts.addToAll
                        : translations.additionalProducts.add}
                    </>
                  )}
                </Button>
              </Stack>
            </Box>
            {product.scope === "PASSENGER" && travelers.length > 1 && (
              <Box sx={sxStyles.productPassengerContainer}>
                {travelers.map((traveler, travelerIndex) => (
                  <Box key={`traveler-productline-${travelerIndex}`}>
                    {isProductAvailableForTraveler(product, traveler) && (
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        gap={2}
                        sx={{
                          py: 1,
                          pl: 6,
                        }}
                      >
                        <Stack>
                          <Typography variant="titleMedium" weight="bold">
                            {`${traveler.formFields.firstName} ${traveler.formFields.lastName}`}
                          </Typography>
                          <Typography variant="bodyMedium">
                            +
                            {formatPrice(
                              calcProductPricePerPassenger(product, traveler),
                              locale,
                              product.currency,
                              false,
                            )}
                          </Typography>
                        </Stack>
                        <FormControlLabel
                          control={
                            <Checkbox
                              onChange={() =>
                                handleToggleProduct(
                                  product,
                                  productIndex,
                                  false,
                                  travelerIndex,
                                )
                              }
                              checked={product.selectedList.includes(
                                travelerIndex,
                              )}
                              inputProps={{ "aria-label": "controlled" }}
                            />
                          }
                          label={
                            <Typography variant="bodyMedium">
                              {translations.additionalProducts.add}
                            </Typography>
                          }
                        />
                      </Stack>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        ))}
      </Box>
      <Box sx={sxStyles.nextStepButtonContainer}>
        <Button
          color="primary"
          size={isDesktop ? "large" : "xLarge"}
          variant={isDesktop ? "outlined" : "contained"}
          fullWidth={!isDesktop}
          onClick={clickNextButton}
        >
          {isValid
            ? translations.navigation.nextStep
            : translations.navigation.skipThisStep}
        </Button>
      </Box>
      {isDesktop && showInfo !== "" && (
        <Popover
          open
          onClose={() => setShowInfo("")}
          anchorEl={elementRefs.current[showInfo]}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          sx={sxStyles.popover}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
        >
          {<RichText htmlContent={getProductContent(showInfo, "info")} />}
        </Popover>
      )}
      {!isDesktop && (
        <Drawer
          anchor="bottom"
          open={showInfo !== ""}
          onClose={() => setShowInfo("")}
        >
          <Box sx={sxStyles.productInfoDrawer}>
            <IconButton className="closeButton" onClick={() => setShowInfo("")}>
              <CloseIcon fontSize="medium" color="primary" />
            </IconButton>
            <Typography variant="h5" color={theme.palette.primary.main}>
              {getProductContent(showInfo, "title")}
            </Typography>

            <RichText htmlContent={getProductContent(showInfo, "info")} />
          </Box>
        </Drawer>
      )}
    </Box>
  );
}

export const sxStyles: { [key: string]: SxProps } = {
  formWrapper: {
    borderBottom: "1px solid ",
    borderBottomColor: "grey.300",
    mb: 4,
    mt: { xs: 2, md: 4 },
    p: { xs: 2, md: 4 },
    backgroundColor: { md: "grey.100" },
  },

  description: {
    p: { xs: 2, md: 0 },
    m: { xs: 2, md: 0 },
    backgroundColor: { xs: "grey.100", md: "rgba(0,0,0,0)" },
  },
  productItem: {
    mb: {
      xs: 2,
      sm: 4,
    },
  },
  productItemHeader: {
    width: 1,
    flexDirection: "row",
    flexWrap: "nowrap",
    alignItems: "start",
  },
  productItemTitle: {
    flexGrow: 1,
    "& svg": {
      position: "absolute",
      color: "black",
    },
  },
  productItemPrice: {
    ml: 1,
  },
  productInfoDrawer: {
    py: 4,
    px: 2,
    "& h5": {
      mb: 3,
    },
  },
  productItemIcon: {
    mr: 1,
    lineHeight: 0,
  },
  productRow: {
    pt: 1,
    pl: 4,
  },
  productButton: {
    mt: 1,
    ml: 2,
    textAlign: "right",
    flexShrink: 0,
  },
  productPassengerContainer: {
    backgroundColor: "grey.50",
  },

  contactFormWrapper: {
    p: { xs: 2, md: 0 },
    display: "grid",
    gap: 2,
    gridTemplateColumns: { xs: "repeat(1, 1fr)", sm: "repeat(2, 1fr)" },
    mb: 2,
    border: { md: 0 },
  },
  skipStepButtonContainer: {
    px: 2,
  },
  nextStepButtonContainer: {
    px: 2,
    pb: 4,
    display: { md: "flex" },
    padding: { md: 0 },
    justifyContent: { md: "flex-end" },
  },
  popover: {
    p: 32,
    "& .MuiPopover-paper": {
      maxWidth: "380px",
      p: 2,
    },
  },
};
